import { deletePlayer } from '@/api/player-list/deletePlayer';
import { GetPlayerDetailResponse } from '@/api/type/types';
import DeleteUserModal from '@/components/modal/DeleteUserModal';
import { useAppDispatch } from '@/stores/hooks';
import { player } from '@/stores/slices/player-list/playerSlice';
import { fonts } from '@/theme/fonts';
import { formatDate } from '@/utils/date-time/formatDate';
import { getCountryNameByCode } from '@/utils/name/getCountryName';
import { formatInternationalNumber } from '@/utils/number/formatInternationalNumber';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

interface PlayerDetailProps {
  detail: GetPlayerDetailResponse;
}
const PlayerDetail = ({ detail }: PlayerDetailProps) => {
  const { t } = useTranslation();
  const [deletePlayerId, setDeletePlayerId] = useState<string>('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  const onHandleDelete = useCallback(
    (playerId: string) => () => {
      setDeletePlayerId(playerId);
      setIsDeleteModalOpen(true);
    },
    []
  );

  const onConfirmDelete = useCallback(
    async (remark: string) => {
      const ERROR_MESSAGE: Record<string, string> = {
        E_MEMBER_002: t('deleteAccount.notFound'),
        E_WITHDRAW_001: t('deleteAccount.hasCloudOrderData'),
        E_WITHDRAW_002: t('deleteAccount.hasScannerData'),
        E_WITHDRAW_003: t('deleteAccount.hasChatData'),
        E_WITHDRAW_004: t('deleteAccount.hasPingPongOrderData'),
      };
      try {
        const res = await deletePlayer({
          playerId: deletePlayerId,
          remark: remark,
        });
        if (res.isSuccess) {
          alert(`${detail.loginId} ${t('deleteAccount.deleteSuccess')}`);
          dispatch(player.removePlayer(res.data.memberId));
          setIsDeleteModalOpen(false);
          return;
        }
        alert(ERROR_MESSAGE[res.error.code] ?? t('deleteAccount.deleteFailed'));
      } catch (e) {
        console.error(e);
        alert(t('deleteAccount.deleteFailed'));
      }
    },
    [deletePlayerId, detail.loginId, dispatch, t]
  );

  const onCloseModal = useCallback(() => {
    setIsDeleteModalOpen(false);
  }, []);

  return (
    <Wrapper>
      <Table>
        <tbody>
          <tr>
            <LabelTd>ID</LabelTd>
            <ValueTd>
              <DataWrap>{detail.loginId}</DataWrap>
            </ValueTd>
          </tr>
          <tr>
            <LabelTd>
              {`${t('common.firstName')} / ${t('common.lastName')}`}
            </LabelTd>
            <ValueTd>
              <DataWrap>
                {`${detail.name.firstName} / ${detail.name.lastName}`}
              </DataWrap>
            </ValueTd>
          </tr>
          <tr>
            <LabelTd>{t('common.dateOfBirth')}</LabelTd>
            <ValueTd>
              <DataWrap>
                {formatDate({ dateString: detail.birthDate })}
              </DataWrap>
            </ValueTd>
          </tr>
          <tr>
            <LabelTd>{t('common.gender')}</LabelTd>
            <ValueTd>
              <DataWrap>
                {detail.gender === 'MALE'
                  ? t('common.male')
                  : t('common.female')}
              </DataWrap>
            </ValueTd>
          </tr>
          <tr>
            <LabelTd>{t('common.nationality')}</LabelTd>
            <ValueTd>
              <DataWrap>
                {getCountryNameByCode({
                  contactCountryCode: detail.contact.contactCountryCode,
                  countryCode: detail.contact.countryCode,
                })}
              </DataWrap>
            </ValueTd>
          </tr>
          <tr>
            <LabelTd>{t('common.mobileNumber')}</LabelTd>
            <ValueTd>
              <DataWrap>
                {formatInternationalNumber({
                  countryCode: detail.contact.contactCountryCode,
                  number: detail.contact.contactNumber,
                })}
              </DataWrap>
            </ValueTd>
          </tr>
          <tr>
            <LabelTd>{t('playerDetail.clinicNumber')}</LabelTd>
            <ValueTd>
              <DataWrap>
                {formatInternationalNumber({
                  countryCode: detail.clinicContact.contactCountryCode,
                  number: detail.clinicContact.contactNumber,
                })}
              </DataWrap>
            </ValueTd>
          </tr>
          <tr>
            <LabelTd>{t('playerList.officeName')}</LabelTd>
            <ValueTd>
              <DataWrap>{detail.clinicName}</DataWrap>
            </ValueTd>
          </tr>
          <tr>
            <LabelTd>{t('playerDetail.officeAddress')}</LabelTd>
            {detail.contact.contactCountryCode === '+82' && (
              <ValueTd>
                <DataWrap>{detail.clinicAddress.postalCode}</DataWrap>
                <DataWrap>{detail.clinicAddress.address1}</DataWrap>
                <DataWrap>{detail.clinicAddress.address2}</DataWrap>
                <DataWrap>{detail.clinicAddress.city ?? ''}</DataWrap>
                <DataWrap>{detail.clinicAddress.state ?? ''}</DataWrap>
              </ValueTd>
            )}
            {detail.contact.contactCountryCode !== '+82' && (
              <ValueTd>
                <AddressSection>
                  <DataWrap>{detail.clinicAddress.address1}</DataWrap>
                  <DataWrap>{detail.clinicAddress.address2}</DataWrap>
                  <DataWrap>{detail.clinicAddress.city ?? ''}</DataWrap>
                  <DataWrap>{detail.clinicAddress.state ?? ''}</DataWrap>
                  <DataWrap>{detail.clinicAddress.postalCode ?? ''}</DataWrap>
                </AddressSection>
              </ValueTd>
            )}
          </tr>
        </tbody>
      </Table>
      <ButtonSection>
        <DeleteButton onClick={onHandleDelete(detail.id)}>
          {t('deleteAccount.deleteAccount')}
        </DeleteButton>
      </ButtonSection>
      <DeleteUserModal
        open={isDeleteModalOpen}
        onClose={onCloseModal}
        onConfirm={onConfirmDelete}
        role="PLAYER"
      />
    </Wrapper>
  );
};

export default PlayerDetail;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  background: #ffffff;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 8px;
`;

const LabelTd = styled.td`
  width: 140px;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
`;

const ValueTd = styled.td`
  padding: 8px 16px;
  font-size: 14px;
  color: #374151;
`;

const AddressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DataWrap = styled.div`
  min-height: 32px;
  padding: 6px 12px;
  display: flex;
  align-items: center;
  border-radius: 6px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  color: #374151;
  font-size: 14px;
`;

const ButtonSection = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px;
`;

const DeleteButton = styled.button`
  width: 140px;
  height: 36px;
  border-radius: 6px;
  background: #ef4444;
  color: white;
  font-size: 14px;
  font-weight: ${fonts.weight.semiBold};

  &:hover {
    background: #dc2626;
  }
`;
